<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="users" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Loan Guarantor Requests</h3>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="users" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Guarantor Requests ({{guarantorRequests.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search guarantor request" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Guarantor Requests Table -->
          <div v-if="!loading && guarantorRequests.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Applicant</th>
                  <th scope="col" class="fw-semibold">Amount</th>
                  <th scope="col" class="fw-semibold">Purpose</th>
                  <th scope="col" class="fw-semibold">Status</th>
                  <th scope="col" class="fw-semibold">Requested On</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(request, index) in filteredGuarantorRequests" :key="request.id">
                  <td>
                      {{ request.loan.employee.first_name }} {{ request.loan.employee.middle_name }} {{ request.loan.employee.last_name }}
                  </td>
                  <td>{{ formatNumber(request.loan.amount) }}</td>
                  <td>{{ request.loan.purpose }}</td>
                  <td>
                    <span :class="getStatusBadgeClass(request.status)">
                      {{ capitalizeFirstLetter(request.status) }}
                    </span>
                  </td>
                  <td>{{ formatDate(request.created_at) }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary me-1" @click="viewGuarantorRequest(request)">
                      <vue-feather type="eye" size="14"></vue-feather>
                    </button>
                    <button
                      v-if="request.status === 'pending'"
                      class="btn btn-sm btn-outline-success me-1"
                      @click="openApproveModal(request)"
                    >
                      <vue-feather type="check" size="14"></vue-feather>
                    </button>
                    <button
                      v-if="request.status === 'pending'"
                      class="btn btn-sm btn-outline-danger me-1"
                      @click="openRejectModal(request)"
                    >
                      <vue-feather type="x" size="14"></vue-feather>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && guarantorRequests.length === 0" class="text-center py-5">
            <vue-feather type="users" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Guarantor Requests Found</h5>
            <p class="text-muted">You don't have any pending guarantor requests.</p>
          </div>
        </Card>
      </div>
    </div>

    <!-- View Guarantor Request Modal -->
    <Modal
      :open="showViewModal"
      @closed="closeViewModal"
      dialog-size="modal-lg"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Guarantor Request Details
        </h5>
        <button @click="closeViewModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div v-if="selectedRequest">
          <div class="mb-3">
            <h5 class="mb-3">Applicant Information</h5>
            <div class="row">
              <div class="col-md-6 mb-2">
                <strong>Name:</strong> {{ selectedRequest.loan.employee.first_name }} {{ selectedRequest.loan.employee.last_name }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Employee No:</strong> {{ selectedRequest.loan.employee.employee_no }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Department:</strong> {{ selectedRequest.loan.employee.department.name || 'N/A' }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Designation:</strong> {{ selectedRequest.loan.employee.designation.name || 'N/A' }}
              </div>
            </div>
          </div>

          <hr>

          <div class="mb-3">
            <h5 class="mb-3">Loan Information</h5>
            <div class="row">
              <div class="col-md-6 mb-2">
                <strong>Amount:</strong> {{ formatNumber(selectedRequest.loan.amount) }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Maximum Amount:</strong> {{ formatNumber(selectedRequest.loan.max_amount) }}
              </div>
              <div class="col-md-6 mb-2">
                <strong>Status: </strong>
                <span :class="getStatusBadgeClass(selectedRequest.loan.status)">
                  {{ capitalizeFirstLetter(selectedRequest.loan.status) }}
                </span>
              </div>
              <div class="col-md-6 mb-2">
                <strong>Applied On:</strong> {{ formatDate(selectedRequest.loan.created_at) }}
              </div>
              <div class="col-12 mb-2">
                <strong>Purpose:</strong> {{ selectedRequest.loan.purpose || 'N/A' }}
              </div>
            </div>
          </div>

          <hr>

          <div class="mb-3">
            <h5 class="mb-3">Guarantor Status</h5>
            <div class="row">
              <div class="col-md-6 mb-2">
                <strong>Status: </strong>
                <span :class="getStatusBadgeClass(selectedRequest.status)">
                  {{ capitalizeFirstLetter(selectedRequest.status) }}
                </span>
              </div>
              <div class="col-md-6 mb-2">
                <strong>Requested On:</strong> {{ formatDate(selectedRequest.created_at) }}
              </div>
              <div v-if="selectedRequest.status === 'approved'" class="col-md-6 mb-2">
                <strong>Approved On:</strong> {{ formatDate(selectedRequest.approved_at) }}
              </div>
              <div v-if="selectedRequest.status === 'rejected'" class="col-md-6 mb-2">
                <strong>Rejected On:</strong> {{ formatDate(selectedRequest.rejected_at) }}
              </div>
              <div v-if="selectedRequest.status === 'rejected'" class="col-12 mb-2">
                <strong>Rejection Reason:</strong> {{ selectedRequest.rejection_reason || 'N/A' }}
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-4">
            <button type="button" class="btn btn-outline-secondary" @click="closeViewModal">Close</button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Reject Guarantor Request Modal -->
    <Modal
      :open="showRejectModal"
      @closed="closeRejectModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Reject Guarantor Request
        </h5>
        <button @click="closeRejectModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="rejectGuarantorRequest">
          <div class="mb-3">
            <label for="rejection_reason" class="form-label">Reason for Rejection <span class="text-danger">*</span></label>
            <textarea class="form-control" id="rejection_reason" v-model="rejectForm.rejection_reason" rows="3" required></textarea>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeRejectModal">Cancel</button>
            <button type="submit" class="btn btn-danger" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Reject
            </button>
          </div>
        </form>
      </div>
    </Modal>

    <!-- Approve Guarantor Request Modal -->
    <Modal
      :open="showApproveModal"
      @closed="closeApproveModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Approve Guarantor Request
        </h5>
        <button @click="closeApproveModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <p class="text-muted">
          By approving, you agree to guarantee the loan amount of <strong>{{ selectedRequest ? formatNumber(selectedRequest.loan.amount) : '' }}</strong> for
          <strong>{{ selectedRequest ? selectedRequest.loan.employee.first_name + ' ' + selectedRequest.loan.employee.last_name : '' }}</strong>.
        </p>
        <hr>
        <h5 class="mb-3">Loan Terms & Conditions</h5>
        <p>
          I hereby accept, liability for the repayment of the advance in the event of the borrower’s default. I understand that the amount in the default
          may be recovered by attachment of my property or salary and that I shall not be eligible for loans or advances unless the amount in default has been cleared in full.
        </p>
        <hr>
        <p>
          By clicking "Approve" below, I agree to all terms and conditions of this agreement as listed.
        </p>
        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-outline-secondary me-2" @click="closeApproveModal">Cancel</button>
          <button type="button" class="btn btn-success" @click="approveGuarantorRequest" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Approve
          </button>
        </div>
      </div>
    </Modal>

  </div>
</template>

<script>
import loanService from '@/api/loanService';
import {decrypt} from '@/utils/crypto';
import notify from '@/utils/notify';
import formatDate from '@/utils/formatDate';
import VueFeather from 'vue-feather';
import confirmDialog from "@/utils/confirmDialog";
import formatNumber from "../../utils/formatNumber";

export default {
  name: 'GuarantorRequests',
  components: { VueFeather },
  data() {
    return {
      loading: true,
      saving: false,
      guarantorRequests: [],
      dataTableSearch: '',
      showViewModal: false,
      showRejectModal: false,
      showApproveModal: false,
      selectedRequest: null,
      profile: {},
      rejectForm: {
        rejection_reason: ''
      }
    };
  },

  methods: {
    formatNumber,
    formatDate,
    confirmDialog,
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case 'pending':
          return 'badge bg-warning';
        case 'approved':
          return 'badge bg-success';
        case 'rejected':
          return 'badge bg-danger';
        default:
          return 'badge bg-primary';
      }
    },
    async fetchGuarantorRequests() {
      try {
        let employeeId = this.profile.employee_id
        this.guarantorRequests = await loanService.getMyGuarantorRequests(employeeId);
      } catch (error) {
        notify.alert.error('Failed to fetch guarantor requests');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    viewGuarantorRequest(request) {
      this.selectedRequest = request;
      this.showViewModal = true;
    },
    closeViewModal() {
      this.showViewModal = false;
      this.selectedRequest = null;
    },
    openRejectModal(request) {
      this.selectedRequest = request;
      this.rejectForm.rejection_reason = '';
      this.showRejectModal = true;
    },
    closeRejectModal() {
      this.showRejectModal = false;
      this.selectedRequest = null;
    },
    openApproveModal(request) {
      this.selectedRequest = request;
      this.showApproveModal = true;
    },
    closeApproveModal() {
      this.showApproveModal = false;
      this.selectedRequest = null;
    },
    async approveGuarantorRequest() {
      if (!this.selectedRequest) return;

      try {
        this.saving = true;
        await loanService.approveGuarantor(this.selectedRequest.id);
        this.closeApproveModal();
        await this.fetchGuarantorRequests();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to approve guarantor request');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    async rejectGuarantorRequest() {
      if (!this.rejectForm.rejection_reason) {
        notify.alert.error('Please provide a reason for rejection');
        return;
      }

      try {
        this.saving = true;
        await loanService.rejectGuarantor(this.selectedRequest.id, this.rejectForm);
        this.closeRejectModal();
        await this.fetchGuarantorRequests();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to reject guarantor request');
        console.error(error);
      } finally {
        this.saving = false;
      }
    }
  },

  computed: {
    filteredGuarantorRequests() {
      if (!this.dataTableSearch) return this.guarantorRequests;

      const search = this.dataTableSearch.toLowerCase();
      return this.guarantorRequests.filter(request =>
          (request.loan.employee.first_name + ' ' + request.loan.employee.last_name).toLowerCase().includes(search) ||
          request.loan.purpose.toLowerCase().includes(search) ||
          request.status.toLowerCase().includes(search)
      );
    }
  },

  created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.fetchGuarantorRequests();
  }
};
</script>
